<script setup lang="ts">
import { API_ITEM_TYPE, VARIANT_ITEM_TYPE } from '~/shared/constants'
import { groupItemIcons } from '~/shared/allergenHierarchy'
import type { DishIconChip } from '~/shared/allergenHierarchy'

const props = defineProps<{
  item: MenuItemDetail
}>()

const { t } = useI18n()

const { styles } = useVariants('menuItem', {
  itemType:
    props.item.itemType === API_ITEM_TYPE.FOOD
      ? VARIANT_ITEM_TYPE.DINNER
      : VARIANT_ITEM_TYPE.DRINKS,
})

const iconChips = computed(() => groupItemIcons(props.item.icons))

function iconLabel(id: string): string {
  const key = `allergy.${id}`
  const translated = t(key)
  return translated === key ? id : translated
}

function chipLabel(chip: DishIconChip): string {
  const parent = iconLabel(chip.iconId)
  if (chip.subIds.length === 0) return parent
  return `${parent}: ${chip.subIds.map(iconLabel).join(', ')}`
}
</script>

<template>
  <div
    :data-item-type="item.itemType"
    :class="[styles.base(), item.label ? styles.labelBase() : '']"
  >
    <!-- Title -->
    <h3 :class="styles.title()">
      {{ item.title }}
    </h3>

    <!-- Prices -->
    <ul v-if="item.prices.length" :class="styles.priceList()">
      <li
        v-for="price in item.prices"
        :key="price.amount"
        :class="styles.priceItem()"
      >
        <span v-if="price.label">{{ price.label }} - </span>
        {{ price.amount }}
      </li>
    </ul>

    <!-- Icons -->
    <div v-if="iconChips.length" :class="styles.icons()">
      <MenuItemIcon
        v-for="chip in iconChips"
        :key="chip.iconId"
        :icon-id="chip.iconId"
        :label="chipLabel(chip)"
        :badge="chip.subIds.length"
      />
    </div>

    <!-- Description -->
    <p v-if="item.description" :class="styles.description()">
      {{ item.description }}
    </p>

    <!-- Label (footnote) -->
    <p v-if="item.label" :class="styles.label()">
      {{ item.label }}
    </p>
  </div>
</template>
